import {
    useCallback, useEffect, useRef
} from 'react';
import {
    debounce
} from 'shared/utils/debounce';

/**
 * Hook to detect when scrolling reaches the end of a container
 * @param {number} threshold - Distance from bottom to trigger (default: 50px)
 * @param {number} delay - Debounce delay in ms (default: 100ms)
 * @param {Function} onEndReached - Callback function when end is reached
 */
export const useScrollEndDetect = (threshold = 50, delay = 100, onEndReached) => {
    const debouncedCallback = useRef();

    useEffect(() => {
        debouncedCallback.current = debounce((isAtEnd) => {
            if (isAtEnd && onEndReached) {
                onEndReached();
            }
        }, delay);

        return () => {
            debouncedCallback.current?.cancel();
        };
    }, [delay, onEndReached]);

    const handleScroll = useCallback((event) => {
        const {
            scrollHeight, scrollTop, clientHeight
        } = event.currentTarget;
        const isAtEnd = scrollHeight - scrollTop <= clientHeight + threshold;

        if (debouncedCallback.current) {
            debouncedCallback.current(isAtEnd);
        }
    }, [threshold]);

    return handleScroll;
};
